import { useState, useMemo, useEffect, useRef } from 'react'
import { PurchaseRequest, Center } from '../types'
import { loadCenters } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { rejectPurchaseRequestByVedouci } from '../lib/apiPhpAdapter'
import Pagination from './Pagination'
import { highlightTextMultiple } from '../utils/textHighlight'
import { protectedFileHref } from '../utils/fileDownload'
import { useIsMobile } from '../hooks/useMediaQuery'
import { DetailsModal } from './DetailsModal'
import { CancelRequestModal } from './CancelRequestModal'
import { getPurchaseStatusLabel, getPurchaseStatusClass } from '../lib/purchaseRequestStatus'
import './PurchaseRequestsTable.css'
import '../styles/common-buttons.css'

interface PurchaseRequestsTableProps {
  requests: PurchaseRequest[]
  onAction?: (request: PurchaseRequest, action: string) => void
  onPrint?: (request: PurchaseRequest) => void
  filterType?: 'vedouci' | 'financist' | 'director' | 'referent' | 'admin'
  hideStatusFilter?: boolean
  statusFilter?: string | null
}

function PurchaseRequestsTable({ requests, onAction, onPrint, filterType = 'vedouci', hideStatusFilter = false, statusFilter: externalStatusFilter }: PurchaseRequestsTableProps) {
  const { user } = useAuth()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  
  // Если передан внешний фильтр, используем его вместо внутреннего
  const effectiveStatusFilter = externalStatusFilter !== undefined 
    ? (externalStatusFilter === null ? 'all' : externalStatusFilter)
    : statusFilter
  const [centerFilter, setCenterFilter] = useState<string>('all')
  const [textFilter, setTextFilter] = useState<string>('')
  
  const [centers, setCenters] = useState<Center[]>([])
  const [loadingFilters, setLoadingFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100
  
  // Состояние модального окна
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  // Состояние модального окна отмены
  const [cancelModalRequest, setCancelModalRequest] = useState<PurchaseRequest | null>(null)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  
  // Refs для синхронизации прокрутки
  const topScrollRef = useRef<HTMLDivElement>(null)
  const bottomScrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadFilterData = async () => {
      if (filterType === 'financist' || filterType === 'director' || filterType === 'referent' || filterType === 'admin') {
        try {
          setLoadingFilters(true)
          const centersData = await loadCenters()
          setCenters(centersData)
        } catch (error) {
          console.error('Chyba při načítání středisek:', error)
        } finally {
          setLoadingFilters(false)
        }
      }
    }

    loadFilterData()
  }, [filterType])

  // Сбрасываем страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1)
  }, [effectiveStatusFilter, centerFilter, textFilter])

  const getStatusLabel = getPurchaseStatusLabel

  const getStatusClass = getPurchaseStatusClass

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Получаем дату последнего изменения статуса

  // Фильтрация и сортировка
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...requests]

    // Фильтр по статусу
    if (effectiveStatusFilter !== 'all') {
      if (effectiveStatusFilter === 'approved') {
        filtered = filtered.filter(r => r.status === 'approved_by_economist' || r.status === 'approved_by_director')
      } else if (effectiveStatusFilter === 'rejected') {
        filtered = filtered.filter(r => r.status === 'rejected' || r.status === 'rejected_by_director' || r.status === 'rejected_by_economist')
      } else {
        filtered = filtered.filter(r => r.status === effectiveStatusFilter)
      }
    }

    // Фильтр по центру
    if ((filterType === 'financist' || filterType === 'director' || filterType === 'referent' || filterType === 'admin') && centerFilter !== 'all') {
      filtered = filtered.filter(r => r.center_id === centerFilter)
    }

    // Текстовый поиск
    if (textFilter.trim() !== '') {
      const searchLower = textFilter.toLowerCase()
      filtered = filtered.filter(r => 
        r.subject.toLowerCase().includes(searchLower) ||
        r.description.toLowerCase().includes(searchLower) ||
        r.reason.toLowerCase().includes(searchLower) ||
        (r.supplier && r.supplier.toLowerCase().includes(searchLower)) ||
        (r.notes && r.notes.toLowerCase().includes(searchLower))
      )
    }

    // Сортировка - всегда новые сверху (по created_at DESC)
    filtered.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime()
      const bTime = new Date(b.created_at).getTime()
      return bTime - aTime // DESC - новые сверху
    })

    return filtered
  }, [requests, effectiveStatusFilter, centerFilter, textFilter, filterType])

  // Синхронизация прокрутки сверху и снизу
  useEffect(() => {
    const topScroll = topScrollRef.current
    const bottomScroll = bottomScrollRef.current
    
    if (!topScroll || !bottomScroll) return

    // Синхронизируем ширину spacer с реальной таблицей
    const table = bottomScroll.querySelector('.purchase-requests-table') as HTMLTableElement
    if (table) {
      const spacer = topScroll.querySelector('.table-scroll-spacer') as HTMLDivElement
      if (spacer) {
        spacer.style.minWidth = `${table.scrollWidth}px`
      }
    }

    const handleTopScroll = () => {
      if (bottomScroll) {
        bottomScroll.scrollLeft = topScroll.scrollLeft
      }
    }

    const handleBottomScroll = () => {
      if (topScroll) {
        topScroll.scrollLeft = bottomScroll.scrollLeft
      }
    }

    topScroll.addEventListener('scroll', handleTopScroll, { passive: true })
    bottomScroll.addEventListener('scroll', handleBottomScroll, { passive: true })

    // Инициализируем синхронизацию при загрузке
    topScroll.scrollLeft = bottomScroll.scrollLeft

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll)
      bottomScroll.removeEventListener('scroll', handleBottomScroll)
    }
  }, [filteredAndSortedRequests, currentPage])

  // Сброс фильтров
  const resetFilters = () => {
    setStatusFilter('all')
    setCenterFilter('all')
    setTextFilter('')
  }

  const handleRowClick = (request: PurchaseRequest) => {
    setSelectedRequest(request)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedRequest(null)
  }

  // Проверка, может ли пользователь отменить заявку
  const canCancelRequest = (request: PurchaseRequest): boolean => {
    if (!user || filterType !== 'vedouci') return false
    // Проверяем, что заявка создана текущим пользователем
    if (request.created_by_id !== user.id) return false
    return request.status === 'pending_leader'
  }

  // Обработчик открытия модального окна отмены
  const handleCancelClick = (e: React.MouseEvent, request: PurchaseRequest) => {
    e.stopPropagation()
    setCancelModalRequest(request)
    setIsCancelModalOpen(true)
  }

  // Обработчик подтверждения отмены
  const handleConfirmCancel = async (reason: string) => {
    if (!cancelModalRequest) return

    try {
      await rejectPurchaseRequestByVedouci(cancelModalRequest.id, reason)
      setIsCancelModalOpen(false)
      setCancelModalRequest(null)
      // Обновляем список заявок через onAction, если он передан
      if (onAction) {
        onAction(cancelModalRequest, 'reject-vedouci')
      } else {
        // Если onAction не передан, просто закрываем модальное окно
        // Родительский компонент должен обновить список самостоятельно
        window.location.reload()
      }
    } catch (error: any) {
      alert(error?.message || 'Chyba při zrušení žádosti')
    }
  }

  // Получаем уникальные статусы для фильтра
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(requests.map(r => r.status))
    return Array.from(statuses)
  }, [requests])

  if (requests.length === 0) {
    return (
      <div className="empty-state">
        <p>Žádné žádosti</p>
      </div>
    )
  }

  return (
    <div className="purchase-requests-table-container">
      {/* Фильтры */}
      <div className="table-filters">
        {!hideStatusFilter && (
          <div className="filter-group">
            <label htmlFor="status-filter">Status:</label>
            <select
              id="status-filter"
              value={effectiveStatusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Všechny</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
        )}

        {(filterType === 'financist' || filterType === 'director' || filterType === 'referent' || filterType === 'admin') && (
          <div className="filter-group">
            <label htmlFor="center-filter">Středisko:</label>
            <select
              id="center-filter"
              value={centerFilter}
              onChange={(e) => setCenterFilter(e.target.value)}
              disabled={loadingFilters}
            >
              <option value="all">Všechna</option>
              {centers.map(center => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label htmlFor="text-filter">Vyhledávání:</label>
          <input
            id="text-filter"
            type="text"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            placeholder="Hledat v předmětu, popisu..."
          />
        </div>

        <button className="reset-filters-btn" onClick={resetFilters}>
          Resetovat filtry
        </button>
      </div>

      {/* Таблица */}
      <div className="table-container-wrapper">
        {/* Верхняя прокрутка */}
        <div className="table-scroll-top" ref={topScrollRef}>
          <div className="table-scroll-spacer"></div>
        </div>
        
        {/* Основная таблица */}
        <div className="table-wrapper" ref={bottomScrollRef}>
          <table className="purchase-requests-table">
            <thead>
              <tr>
                <th>Status</th>
                <th className="hidden-column">ID</th>
                <th>Předmět</th>
                <th>Středisko</th>
                <th>Odpovědná osoba</th>
                <th className="hidden-column">E-Mail</th>
                <th className="hidden-column">Telefon</th>
                <th className="description-column">Popis</th>
                <th>Důvod objednání</th>
                <th>Dodavatel</th>
                <th>Částka</th>
                <th>Příloha</th>
                <th className="date-column">Datum vytvoření</th>
                <th className="date-column">Schváleno ekonomem</th>
                <th className="date-column">Odloženo ekonomem</th>
                <th className="date-column">Zamítnuto ekonomem</th>
                <th className="date-column">Schváleno ředitelem</th>
                <th className="date-column">Zamítnuto ředitelem</th>
                <th className="date-column">Přijato referentem</th>
                <th className="date-column">Objednáno referentem</th>
                <th className="date-column">Připraveno referentem</th>
                <th className="date-column">Dokončeno</th>
              <th>Poznámky</th>
              {(filterType === 'vedouci' || filterType === 'financist' || filterType === 'director' || filterType === 'referent') && <th>Akce</th>}
              </tr>
            </thead>
          <tbody>
            {filteredAndSortedRequests.length === 0 ? (
              <tr>
                <td colSpan={filterType === 'financist' || filterType === 'director' || filterType === 'referent' ? 22 : 21} className="empty-row">
                  Žádné žádosti neodpovídají filtrům
                </td>
              </tr>
            ) : (
              filteredAndSortedRequests
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map(request => (
                <tr key={request.id} className="clickable-row" onClick={() => handleRowClick(request)}>
                  <td>
                    <span className={`status-badge ${getStatusClass(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </td>
                  <td className="hidden-column">#{request.id.substring(0, 8)}</td>
                  <td>
                    <div className="request-subject">
                      <strong>{highlightTextMultiple(request.subject, textFilter)}</strong>
                    </div>
                  </td>
                  <td>{highlightTextMultiple(request.center_name || '-', textFilter)}</td>
                  <td>{highlightTextMultiple(request.created_by_name || '-', textFilter)}</td>
                  <td className="hidden-column">{highlightTextMultiple(request.created_by_email || '-', textFilter)}</td>
                  <td className="hidden-column">{highlightTextMultiple(request.phone || '-', textFilter)}</td>
                  <td className="text-cell description-column" title={request.description || '-'}>
                    {highlightTextMultiple(
                      (request.description && request.description.length > 50)
                        ? `${request.description.substring(0, 50)}...`
                        : (request.description || '-'),
                      textFilter
                    )}
                  </td>
                  <td className="text-cell" title={request.reason || '-'}>
                    {highlightTextMultiple(
                      (request.reason && request.reason.length > 40)
                        ? `${request.reason.substring(0, 40)}...`
                        : (request.reason || '-'),
                      textFilter
                    )}
                  </td>
                  <td className="text-cell" title={request.supplier || '-'}>
                    {highlightTextMultiple(
                      (request.supplier && request.supplier.length > 30)
                        ? `${request.supplier.substring(0, 30)}...`
                        : (request.supplier || '-'),
                      textFilter
                    )}
                  </td>
                  <td>{request.amount ? `${request.amount.toLocaleString('cs-CZ')} Kč` : '-'}</td>
                  <td>
                    {request.file_name && request.file_url ? (
                      <a 
                        href={protectedFileHref(request.file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-link"
                      >
                        {request.file_name}
                      </a>
                    ) : '-'}
                  </td>
                  <td className="date-column">{formatDate(request.created_at)}</td>
                  <td className="date-column">{request.approved_by_economist_at ? formatDate(request.approved_by_economist_at) : '-'}</td>
                  <td className="date-column">{request.deferred_at ? formatDate(request.deferred_at) : '-'}</td>
                  <td className="date-column">{request.rejected_by_economist_at ? formatDate(request.rejected_by_economist_at) : '-'}</td>
                  <td className="date-column">{request.approved_by_director_at ? formatDate(request.approved_by_director_at) : '-'}</td>
                  <td className="date-column">{request.rejected_by_director_at ? formatDate(request.rejected_by_director_at) : '-'}</td>
                  <td className="date-column">{request.accepted_by_referent_at ? formatDate(request.accepted_by_referent_at) : '-'}</td>
                  <td className="date-column">{request.ordered_by_referent_at ? formatDate(request.ordered_by_referent_at) : '-'}</td>
                  <td className="date-column">{request.ready_by_referent_at ? formatDate(request.ready_by_referent_at) : '-'}</td>
                  <td className="date-column">{request.completed_at ? formatDate(request.completed_at) : '-'}</td>
                  <td className="text-cell" title={request.notes || '-'}>
                    {highlightTextMultiple(
                      (request.notes && request.notes.length > 40)
                        ? `${request.notes.substring(0, 40)}...`
                        : (request.notes || '-'),
                      textFilter
                    )}
                  </td>
                  {filterType === 'vedouci' && canCancelRequest(request) && (
                    <td>
                      <div className="table-actions" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="btn-reject-small" 
                          onClick={(e) => handleCancelClick(e, request)}
                          title="Zrušit žádost"
                        >
                          Zrušit
                        </button>
                      </div>
                    </td>
                  )}
                  {(filterType === 'financist' || filterType === 'director' || filterType === 'referent') && (
                    <td>
                      {onAction && (
                        <div className="table-actions" onClick={(e) => e.stopPropagation()}>
                          {filterType === 'financist' && request.status === 'new' && (
                            <>
                              <button className="btn-approve-small" onClick={() => onAction(request, 'approve')}>
                                Schválit
                              </button>
                              <button className="btn-reject-small" onClick={() => onAction(request, 'reject')}>
                                Zamítnout
                              </button>
                              <button className="btn-defer-small" onClick={() => onAction(request, 'defer')}>
                                Odložit
                              </button>
                            </>
                          )}
                          {filterType === 'financist' && request.status === 'deferred' && (
                            <>
                              <button className="btn-approve-small" onClick={() => onAction(request, 'approve')}>
                                Schválit
                              </button>
                              <button className="btn-reject-small" onClick={() => onAction(request, 'reject')}>
                                Zamítnout
                              </button>
                            </>
                          )}
                          {filterType === 'director' && request.status === 'approved_by_economist' && (
                            <>
                              <button className="btn-approve-small" onClick={() => onAction(request, 'approve')}>
                                Schválit
                              </button>
                              <button className="btn-reject-small" onClick={() => onAction(request, 'reject')}>
                                Zamítnout
                              </button>
                            </>
                          )}
                          {filterType === 'referent' && (
                            <>
                              {onPrint && (
                                <button 
                                  className="btn-print-small" 
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onPrint(request)
                                  }}
                                  title="Tisk"
                                >
                                  🖨️
                                </button>
                              )}
                              {request.status === 'approved_by_director' && (
                                <button className="btn-accept-small" onClick={() => onAction(request, 'accept')}>
                                  Přijato
                                </button>
                              )}
                              {request.status === 'accepted_by_referent' && (
                                <>
                                  <button className="btn-order-small" onClick={() => onAction(request, 'order')}>
                                    Objednáno
                                  </button>
                                  <button className="btn-reject-small" onClick={() => onAction(request, 'reject-referent')}>
                                    Zamítnout
                                  </button>
                                </>
                              )}
                              {request.status === 'ordered_by_referent' && (
                                <>
                                  <button className="btn-complete-small" onClick={() => onAction(request, 'complete')}>
                                    Hotovo
                                  </button>
                                  <button className="btn-reject-small" onClick={() => onAction(request, 'reject-referent')}>
                                    Zamítnout
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </div>

      {filteredAndSortedRequests.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(filteredAndSortedRequests.length / itemsPerPage)}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredAndSortedRequests.length}
        />
      )}
      
      <div className="table-info">
        Zobrazeno {Math.min((currentPage - 1) * itemsPerPage + 1, filteredAndSortedRequests.length)}-{Math.min(currentPage * itemsPerPage, filteredAndSortedRequests.length)} z {filteredAndSortedRequests.length} žádostí
      </div>

      {/* Модальное окно с деталями */}
      {selectedRequest && (
        <DetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={`Žádost #${selectedRequest.id.substring(0, 8)}`}
          actions={
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {filterType === 'vedouci' && canCancelRequest(selectedRequest) && (
                <button 
                  className="btn-reject-small" 
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsModalOpen(false)
                    setCancelModalRequest(selectedRequest)
                    setIsCancelModalOpen(true)
                  }}
                  title="Zrušit žádost"
                >
                  Zrušit žádost
                </button>
              )}
              {(onAction || onPrint) && (filterType === 'financist' || filterType === 'director' || filterType === 'referent') && (
                <>
                {filterType === 'referent' && onPrint && (
                  <button className="btn-print-small" onClick={() => { onPrint(selectedRequest); handleCloseModal(); }}>
                    🖨️ Tisk
                  </button>
                )}
                {filterType === 'financist' && selectedRequest.status === 'new' && onAction && (
                  <>
                    <button className="btn-approve-small" onClick={() => { onAction(selectedRequest, 'approve'); handleCloseModal(); }}>
                      Schválit
                    </button>
                    <button className="btn-reject-small" onClick={() => { onAction(selectedRequest, 'reject'); handleCloseModal(); }}>
                      Zamítnout
                    </button>
                    <button className="btn-defer-small" onClick={() => { onAction(selectedRequest, 'defer'); handleCloseModal(); }}>
                      Odložit
                    </button>
                  </>
                )}
                {filterType === 'financist' && selectedRequest.status === 'deferred' && onAction && (
                  <>
                    <button className="btn-approve-small" onClick={() => { onAction(selectedRequest, 'approve'); handleCloseModal(); }}>
                      Schválit
                    </button>
                    <button className="btn-reject-small" onClick={() => { onAction(selectedRequest, 'reject'); handleCloseModal(); }}>
                      Zamítnout
                    </button>
                  </>
                )}
                {filterType === 'director' && selectedRequest.status === 'approved_by_economist' && onAction && (
                  <>
                    <button className="btn-approve-small" onClick={() => { onAction(selectedRequest, 'approve'); handleCloseModal(); }}>
                      Schválit
                    </button>
                    <button className="btn-reject-small" onClick={() => { onAction(selectedRequest, 'reject'); handleCloseModal(); }}>
                      Zamítnout
                    </button>
                  </>
                )}
                {filterType === 'referent' && selectedRequest.status === 'approved_by_director' && onAction && (
                  <button className="btn-accept-small" onClick={() => { onAction(selectedRequest, 'accept'); handleCloseModal(); }}>
                    Přijato
                  </button>
                )}
                {filterType === 'referent' && selectedRequest.status === 'accepted_by_referent' && onAction && (
                  <>
                    <button className="btn-order-small" onClick={() => { onAction(selectedRequest, 'order'); handleCloseModal(); }}>
                      Objednáno
                    </button>
                    <button className="btn-reject-small" onClick={() => { onAction(selectedRequest, 'reject-referent'); handleCloseModal(); }}>
                      Zamítnout
                    </button>
                  </>
                )}
                {filterType === 'referent' && selectedRequest.status === 'ordered_by_referent' && onAction && (
                  <>
                    <button className="btn-complete-small" onClick={() => { onAction(selectedRequest, 'complete'); handleCloseModal(); }}>
                      Hotovo
                    </button>
                    <button className="btn-reject-small" onClick={() => { onAction(selectedRequest, 'reject-referent'); handleCloseModal(); }}>
                      Zamítnout
                    </button>
                  </>
                )}
                </>
              )}
            </div>
          }
        >
          <div className="details-section">
            <div className="details-section-title">Základní informace</div>
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Předmět</div>
                <div className="details-value"><strong>{selectedRequest.subject}</strong></div>
              </div>
              <div className="details-item">
                <div className="details-label">Status</div>
                <div className="details-value">
                  <span className={`status-badge ${getStatusClass(selectedRequest.status)}`}>
                    {getStatusLabel(selectedRequest.status)}
                  </span>
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Středisko</div>
                <div className="details-value">{selectedRequest.center_name || '-'}</div>
              </div>
              <div className="details-item">
                <div className="details-label">Částka</div>
                <div className="details-value">
                  {selectedRequest.amount ? `${selectedRequest.amount.toLocaleString('cs-CZ')} Kč` : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Kontaktní informace</div>
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Odpovědná osoba</div>
                <div className="details-value"><strong>{selectedRequest.created_by_name || '-'}</strong></div>
              </div>
              <div className="details-item">
                <div className="details-label">E-Mail</div>
                <div className="details-value">
                  {selectedRequest.created_by_email ? (
                    <a href={`mailto:${selectedRequest.created_by_email}`} className="email-link">
                      {selectedRequest.created_by_email}
                    </a>
                  ) : '-'}
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Telefon</div>
                <div className="details-value">
                  {selectedRequest.phone ? (
                    <a href={`tel:${selectedRequest.phone}`} className="phone-link">
                      {selectedRequest.phone}
                    </a>
                  ) : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Popis</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedRequest.description || '-'}
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Důvod objednání</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedRequest.reason || '-'}
            </div>
          </div>
          {selectedRequest.supplier && (
            <div className="details-section">
              <div className="details-section-title">Dodavatel</div>
              <div className="details-value">{selectedRequest.supplier}</div>
            </div>
          )}
          {selectedRequest.file_name && selectedRequest.file_url && (
            <div className="details-section">
              <div className="details-section-title">Příloha</div>
              <div className="details-value">
                <a
                  href={protectedFileHref(selectedRequest.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  📎 {selectedRequest.file_name}
                </a>
                <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                  <a
                    href={protectedFileHref(selectedRequest.file_url)}
                    download={selectedRequest.file_name}
                    className="file-link"
                    title="Stáhnout soubor"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ⬇️ Stáhnout
                  </a>
                </span>
              </div>
            </div>
          )}
          {selectedRequest.notes && (
            <div className="details-section">
              <div className="details-section-title">Poznámky</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedRequest.notes}
              </div>
            </div>
          )}
          <div className="details-section">
            <div className="details-section-title">Časové údaje</div>
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Datum vytvoření</div>
                <div className="details-value">{formatDate(selectedRequest.created_at)}</div>
              </div>
              {selectedRequest.approved_by_economist_at && (
                <div className="details-item">
                  <div className="details-label">Schváleno ekonomem</div>
                  <div className="details-value">{formatDate(selectedRequest.approved_by_economist_at)}</div>
                </div>
              )}
              {selectedRequest.deferred_at && (
                <div className="details-item">
                  <div className="details-label">Odloženo ekonomem</div>
                  <div className="details-value">{formatDate(selectedRequest.deferred_at)}</div>
                </div>
              )}
              {selectedRequest.rejected_by_economist_at && (
                <div className="details-item">
                  <div className="details-label">Zamítnuto ekonomem</div>
                  <div className="details-value">{formatDate(selectedRequest.rejected_by_economist_at)}</div>
                </div>
              )}
              {selectedRequest.approved_by_director_at && (
                <div className="details-item">
                  <div className="details-label">Schváleno ředitelem</div>
                  <div className="details-value">{formatDate(selectedRequest.approved_by_director_at)}</div>
                </div>
              )}
              {selectedRequest.rejected_by_director_at && (
                <div className="details-item">
                  <div className="details-label">Zamítnuto ředitelem</div>
                  <div className="details-value">{formatDate(selectedRequest.rejected_by_director_at)}</div>
                </div>
              )}
              {selectedRequest.accepted_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Přijato referentem</div>
                  <div className="details-value">{formatDate(selectedRequest.accepted_by_referent_at)}</div>
                </div>
              )}
              {selectedRequest.ordered_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Objednáno referentem</div>
                  <div className="details-value">{formatDate(selectedRequest.ordered_by_referent_at)}</div>
                </div>
              )}
              {selectedRequest.ready_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Připraveno referentem</div>
                  <div className="details-value">{formatDate(selectedRequest.ready_by_referent_at)}</div>
                </div>
              )}
              {selectedRequest.completed_at && (
                <div className="details-item">
                  <div className="details-label">Dokončeno</div>
                  <div className="details-value">{formatDate(selectedRequest.completed_at)}</div>
                </div>
              )}
            </div>
          </div>
        </DetailsModal>
      )}

      {/* Модальное окно отмены заявки */}
      {cancelModalRequest && (
        <CancelRequestModal
          isOpen={isCancelModalOpen}
          onClose={() => {
            setIsCancelModalOpen(false)
            setCancelModalRequest(null)
          }}
          onConfirm={handleConfirmCancel}
          requestSubject={cancelModalRequest.subject}
        />
      )}
    </div>
  )
}

export default PurchaseRequestsTable

